import type { ProfitFactCode, ProfitPlatform } from "../shared/profit-types";

export type ProfitComponentMap = Partial<Record<ProfitFactCode, bigint>>;

export type ProfitCalculation = {
  actualProductIncomeMinor: bigint;
  netOperatingRevenueMinor: bigint;
  baseCostMinor: bigint;
  fulfillmentCostMinor: bigint;
  platformFeeMinor: bigint;
  creatorFeeMinor: bigint;
  activityCostMinor: bigint;
  advertisingCostMinor: bigint;
  finalCostMinor: bigint;
  recoveryMinor: bigint;
  preActivityProfitMinor: bigint;
  activityAfterProfitMinor: bigint;
  advertisingBeforeProfitMinor: bigint;
  advertisingAfterProfitMinor: bigint;
  finalNetProfitMinor: bigint;
  preActivityMarginPercent: number | null;
  activityAfterMarginPercent: number | null;
  advertisingBeforeMarginPercent: number | null;
  advertisingAfterMarginPercent: number | null;
  finalProductMarginPercent: number | null;
  finalOperatingMarginPercent: number | null;
  acosPercent: number | null;
  tacosPercent: number | null;
  breakEvenAcosPercent: number | null;
  breakEvenPriceMinor: bigint | null;
};

const baseCostCodes = ["product_cost", "packaging_cost", "domestic_logistics", "headhaul", "tariff"] as const;
const fulfillmentCodes = ["warehouse_inbound", "warehouse_operation", "last_mile", "fba_fulfillment"] as const;
const platformFeeCodes = ["amazon_commission", "tiktok_commission", "payment_fee"] as const;
const creatorFeeCodes = ["creator_commission", "extra_commission"] as const;
const activityCodes = ["coupon_fee", "deal_fee", "activity_fee"] as const;
const advertisingCodes = ["amazon_ad", "tiktok_ad", "gmv_max", "creator_ad"] as const;
const finalCostCodes = [
  "overseas_storage", "fba_storage", "long_term_storage", "return_loss", "removal_disposal",
  "monthly_subscription", "software_fee", "fx_loss", "other_expense"
] as const;
const recoveryCodes = ["product_cost_recovery", "commission_recovery", "creator_commission_recovery", "logistics_recovery"] as const;

function value(components: ProfitComponentMap, code: ProfitFactCode): bigint {
  return components[code] ?? 0n;
}

function total(components: ProfitComponentMap, codes: readonly ProfitFactCode[]): bigint {
  return codes.reduce((sum, code) => sum + value(components, code), 0n);
}

export function ratioPercent(numerator: bigint, denominator: bigint): number | null {
  if (denominator === 0n) return null;
  const scaledHundredths = numerator * 10_000n / denominator;
  return Number(scaledHundredths) / 100;
}

function ceilDivide(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new Error("除数必须大于0");
  if (numerator <= 0n) return 0n;
  return (numerator + denominator - 1n) / denominator;
}

export function calculateProfit(
  platform: ProfitPlatform,
  components: ProfitComponentMap,
  advertisingSalesMinor = 0n
): ProfitCalculation {
  const grossSales = value(components, "gross_sales");
  const refund = value(components, "refund_amount");
  const discount = platform === "amazon"
    ? value(components, "coupon_discount") + value(components, "promotion_discount")
    : value(components, "merchant_discount");
  const subsidy = platform === "tiktok" ? value(components, "platform_subsidy") : 0n;
  const shipping = value(components, "buyer_shipping");
  const compensation = value(components, "other_compensation");
  const actualProductIncome = grossSales + subsidy - discount - refund;
  const netOperatingRevenue = actualProductIncome + shipping + compensation;
  const baseCost = total(components, baseCostCodes);
  const fulfillmentCost = total(components, fulfillmentCodes);
  const platformFee = total(components, platformFeeCodes);
  const creatorFee = total(components, creatorFeeCodes);
  const activityCost = total(components, activityCodes);
  const advertisingCost = total(components, advertisingCodes);
  const finalCost = total(components, finalCostCodes);
  const recovery = total(components, recoveryCodes);
  const preActivityRevenue = grossSales + subsidy - refund + shipping + compensation;
  const preActivityProfit = preActivityRevenue - baseCost - fulfillmentCost - platformFee - creatorFee;
  const activityAfterProfit = netOperatingRevenue - baseCost - fulfillmentCost - platformFee - creatorFee - activityCost;
  const advertisingAfterProfit = activityAfterProfit - advertisingCost;
  const finalNetProfit = advertisingAfterProfit - finalCost + recovery;
  const variableObserved = discount + refund + platformFee + creatorFee + activityCost + advertisingCost;
  const fixedNetCost = baseCost + fulfillmentCost + finalCost - subsidy - shipping - compensation - recovery;
  const breakEvenDenominator = grossSales - variableObserved;
  const breakEvenPrice = grossSales > 0n && breakEvenDenominator > 0n
    ? ceilDivide(fixedNetCost * grossSales, breakEvenDenominator)
    : null;

  return {
    actualProductIncomeMinor: actualProductIncome,
    netOperatingRevenueMinor: netOperatingRevenue,
    baseCostMinor: baseCost,
    fulfillmentCostMinor: fulfillmentCost,
    platformFeeMinor: platformFee,
    creatorFeeMinor: creatorFee,
    activityCostMinor: activityCost,
    advertisingCostMinor: advertisingCost,
    finalCostMinor: finalCost,
    recoveryMinor: recovery,
    preActivityProfitMinor: preActivityProfit,
    activityAfterProfitMinor: activityAfterProfit,
    advertisingBeforeProfitMinor: activityAfterProfit,
    advertisingAfterProfitMinor: advertisingAfterProfit,
    finalNetProfitMinor: finalNetProfit,
    preActivityMarginPercent: ratioPercent(preActivityProfit, grossSales),
    activityAfterMarginPercent: ratioPercent(activityAfterProfit, actualProductIncome),
    advertisingBeforeMarginPercent: ratioPercent(activityAfterProfit, actualProductIncome),
    advertisingAfterMarginPercent: ratioPercent(advertisingAfterProfit, actualProductIncome),
    finalProductMarginPercent: ratioPercent(finalNetProfit, actualProductIncome),
    finalOperatingMarginPercent: ratioPercent(finalNetProfit, netOperatingRevenue),
    acosPercent: platform === "amazon" ? ratioPercent(advertisingCost, advertisingSalesMinor) : null,
    tacosPercent: platform === "amazon" ? ratioPercent(advertisingCost, actualProductIncome) : null,
    breakEvenAcosPercent: platform === "amazon" && activityAfterProfit > 0n ? ratioPercent(activityAfterProfit, advertisingSalesMinor) : null,
    breakEvenPriceMinor: breakEvenPrice
  };
}

export type CreatorProjectCalculation = {
  totalInvestmentMinor: bigint;
  projectProfitMinor: bigint;
  projectRoiPercent: number | null;
  advertisingRoiPercent: number | null;
  roas: number | null;
  breakEvenRoas: number | null;
  breakEvenCreatorCommissionPercent: number | null;
  paidBack: boolean;
};

export function calculateCreatorProject(input: {
  orderProfitBeforeProjectAdvertisingMinor: bigint;
  gmvMinor: bigint;
  commissionableGmvMinor: bigint;
  creatorCommissionMinor: bigint;
  advertisingMinor: bigint;
  sampleProductCostMinor: bigint;
  sampleShippingMinor: bigint;
  slotFeeMinor: bigint;
}): CreatorProjectCalculation {
  const investment = input.advertisingMinor + input.sampleProductCostMinor + input.sampleShippingMinor + input.slotFeeMinor;
  const projectProfit = input.orderProfitBeforeProjectAdvertisingMinor - investment;
  const maximumCommission = input.orderProfitBeforeProjectAdvertisingMinor + input.creatorCommissionMinor - investment;
  return {
    totalInvestmentMinor: investment,
    projectProfitMinor: projectProfit,
    projectRoiPercent: ratioPercent(projectProfit, investment),
    advertisingRoiPercent: ratioPercent(input.orderProfitBeforeProjectAdvertisingMinor - input.advertisingMinor, input.advertisingMinor),
    roas: input.advertisingMinor === 0n ? null : Number(input.gmvMinor * 10_000n / input.advertisingMinor) / 10_000,
    breakEvenRoas: input.orderProfitBeforeProjectAdvertisingMinor <= 0n
      ? null
      : Number(input.gmvMinor * 10_000n / input.orderProfitBeforeProjectAdvertisingMinor) / 10_000,
    breakEvenCreatorCommissionPercent: ratioPercent(maximumCommission > 0n ? maximumCommission : 0n, input.commissionableGmvMinor),
    paidBack: projectProfit >= 0n
  };
}

export function allocateLargestRemainder<T extends string>(
  totalMinor: bigint,
  targets: Array<{ id: T; weight: bigint }>
): Array<{ id: T; amountMinor: bigint }> {
  if (!targets.length) throw new Error("至少需要一个分摊目标");
  if (targets.some((target) => target.weight < 0n)) throw new Error("分摊权重不能为负数");
  const weightTotal = targets.reduce((sum, target) => sum + target.weight, 0n);
  if (weightTotal === 0n) throw new Error("分摊权重合计不能为0");
  const sign = totalMinor < 0n ? -1n : 1n;
  const absoluteTotal = totalMinor < 0n ? -totalMinor : totalMinor;
  const rows = targets.map((target, index) => {
    const product = absoluteTotal * target.weight;
    return {
      id: target.id,
      index,
      amountMinor: product / weightTotal,
      remainder: product % weightTotal
    };
  });
  let undistributed = absoluteTotal - rows.reduce((sum, row) => sum + row.amountMinor, 0n);
  const priority = [...rows].sort((left, right) => {
    if (left.remainder === right.remainder) return left.index - right.index;
    return left.remainder > right.remainder ? -1 : 1;
  });
  for (let index = 0; undistributed > 0n; index += 1) {
    priority[index % priority.length].amountMinor += 1n;
    undistributed -= 1n;
  }
  return rows.map((row) => ({ id: row.id, amountMinor: row.amountMinor * sign }));
}

export function marginTone(percent: number | null): "green" | "yellow" | "orange" | "red" | "neutral" {
  if (percent === null) return "neutral";
  if (percent >= 20) return "green";
  if (percent >= 10) return "yellow";
  if (percent >= 0) return "orange";
  return "red";
}
